export const getOrdinalSuffix = (value: number) => {
  const normalizedValue = Math.abs(Math.trunc(value));
  const lastTwoDigits = normalizedValue % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return 'th';
  }

  switch (normalizedValue % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const formatDateWithOrdinalMonthYear = (isoDate: string, locale: string) => {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }

  const day = parsed.getDate();
  const month = parsed.toLocaleDateString(locale, { month: 'short' });
  const year = parsed.getFullYear();

  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const wallClockMsElapsedToday = (now: Date, timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(now);
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value ?? 0);

  return ((read('hour') % 24) * 3600 + read('minute') * 60 + read('second')) * 1000 + now.getMilliseconds();
};

/**
 * Milliseconds from `now` until the next local midnight in `timeZone` (an IANA name), falling back
 * to the browser's zone when the name is unknown. On a DST day this is up to an hour off; callers
 * that re-arm after firing self-correct.
 */
export const msUntilNextMidnight = (timeZone: string | undefined, now: Date = new Date()) => {
  let elapsed: number;
  try {
    elapsed = wallClockMsElapsedToday(now, timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    elapsed = ((now.getHours() * 60 + now.getMinutes()) * 60 + now.getSeconds()) * 1000 + now.getMilliseconds();
  }

  return MS_PER_DAY - elapsed;
};
